import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'src/redux/store';
import useGetCurrentUserId from 'src/hooks/useGetCurrentUserId';
import {
    getTablePreferences,
    handleTablePreferences,
    handleTablePreferencesEdit,
} from 'src/redux/slices/tablePreferences';

const PAGE_SIZES = [25, 50, 100];
const MIN_COLUMN_WIDTH = 70;
const MAX_COLUMN_WIDTH = 800;
const CONFIG_KEYS = new Set(['columns', 'requiredFields']);
const PREFERENCE_KEYS = new Set([
    'columnOrder',
    'columnVisibilityModel',
    'columnWidths',
    'pinnedColumns',
    'sortModel',
    'filterModel',
    'paginationModel',
]);
const ALLOWED_FILTER_OPERATORS = new Set([
    'contains',
    'equals',
    'startsWith',
    'endsWith',
    'isEmpty',
    'isNotEmpty',
    'isAnyOf',
    'is',
    'not',
    'after',
    'onOrAfter',
    'before',
    'onOrBefore',
    '=',
    '!=',
    '>',
    '>=',
    '<',
    '<=',
]);

const cloneValue = (value) => {
    if (value === undefined) return undefined;
    return JSON.parse(JSON.stringify(value));
};

const normalizedIdentifier = (value) => {
    if (value === null || value === undefined) return null;
    const normalized = String(value).trim();
    return normalized || null;
};

export const sanitizeCampaignGroups = (value) => {
    const groupIds = new Set();
    return (Array.isArray(value) ? value : []).reduce((groups, candidate) => {
        if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return groups;
        const id = normalizedIdentifier(candidate.id);
        const label = normalizedIdentifier(candidate.label);
        if (!id || !label || groupIds.has(id) || !Array.isArray(candidate.members)) return groups;

        const memberKeys = new Set();
        const members = candidate.members.reduce((result, member) => {
            if (!member || typeof member !== 'object' || Array.isArray(member)) return result;
            const practiceId = normalizedIdentifier(member.practiceId);
            const campaignId = normalizedIdentifier(member.campaignId);
            if (!practiceId || !campaignId) return result;
            const memberKey = `${practiceId}\u0000${campaignId}`;
            if (memberKeys.has(memberKey)) return result;
            memberKeys.add(memberKey);
            result.push({ practiceId, campaignId });
            return result;
        }, []);
        if (new Set(members.map((member) => member.practiceId)).size < 2) return groups;

        groupIds.add(id);
        groups.push({ id, label, members });
        return groups;
    }, []);
};

const listRecords = (value) => {
    if (Array.isArray(value)) return value;
    return Array.isArray(value?.results) ? value.results : [];
};

const recordFor = (records, tableName) => (
    listRecords(records).find((record) => record?.table_name === tableName) || null
);

const finiteNumber = (value) => {
    if (value === null || value === undefined || value === '' || typeof value === 'boolean') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const uniqueKnownFields = (values, fields) => {
    const known = new Set(fields);
    const seen = new Set();
    return (Array.isArray(values) ? values : []).filter((field) => {
        if (!known.has(field) || seen.has(field)) return false;
        seen.add(field);
        return true;
    });
};

const getColumnFields = (defaults) => {
    const supplied = Array.isArray(defaults?.columns)
        ? defaults.columns.map((column) => (typeof column === 'string' ? column : column?.field))
        : [];
    return uniqueKnownFields(
        supplied.length ? supplied : defaults?.columnOrder,
        supplied.length ? supplied : (defaults?.columnOrder || [])
    );
};

const getRequiredFields = (defaults, fields) => {
    const configured = Array.isArray(defaults?.requiredFields) ? defaults.requiredFields : [];
    const columnRequired = (defaults?.columns || [])
        .filter((column) => typeof column === 'object' && (
            column.required === true || column.identity === true || column.hideable === false
        ))
        .map((column) => column.field);
    return uniqueKnownFields([...configured, ...columnRequired], fields);
};

const sanitizeVisibility = (candidate, defaults, fields, requiredFields) => {
    const model = {};
    fields.forEach((field) => {
        const saved = candidate?.[field];
        const fallback = defaults?.[field];
        model[field] = true;
        if (typeof fallback === 'boolean') model[field] = fallback;
        if (typeof saved === 'boolean') model[field] = saved;
    });
    requiredFields.forEach((field) => { model[field] = true; });
    return model;
};

const sanitizeWidths = (candidate, defaults, fields) => {
    const widths = {};
    fields.forEach((field) => {
        const saved = finiteNumber(candidate?.[field]);
        const fallback = finiteNumber(defaults?.[field]);
        let value = MIN_COLUMN_WIDTH;
        if (fallback !== null) value = fallback;
        if (saved !== null) value = saved;
        widths[field] = Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, Math.round(value)));
    });
    return widths;
};

const sanitizePins = (candidate, defaults, fields, requiredFields) => {
    const source = candidate && typeof candidate === 'object' ? candidate : defaults;
    const left = uniqueKnownFields(source?.left, fields).filter((field) => !requiredFields.includes(field));
    const leftWithIdentity = [...requiredFields, ...left];
    const leftSet = new Set(leftWithIdentity);
    const right = uniqueKnownFields(source?.right, fields).filter((field) => !leftSet.has(field));
    return { left: leftWithIdentity, right };
};

const sanitizeSortModel = (candidate, defaults, fields) => {
    const source = Array.isArray(candidate) ? candidate : defaults;
    const seen = new Set();
    return (Array.isArray(source) ? source : []).reduce((result, item) => {
        if (!fields.includes(item?.field) || !['asc', 'desc'].includes(item?.sort) || seen.has(item.field)) {
            return result;
        }
        seen.add(item.field);
        result.push({ field: item.field, sort: item.sort });
        return result;
    }, []);
};

const sanitizeFilterModel = (candidate, defaults, fields) => {
    const source = candidate && typeof candidate === 'object' ? candidate : (defaults || {});
    const items = (Array.isArray(source.items) ? source.items : []).filter((item) => (
        fields.includes(item?.field) && ALLOWED_FILTER_OPERATORS.has(item?.operator)
    )).map((item) => ({
        ...(item.id === undefined ? {} : { id: item.id }),
        field: item.field,
        operator: item.operator,
        ...(item.value === undefined ? {} : { value: item.value }),
    }));
    const result = {
        logicOperator: ['and', 'or'].includes(source.logicOperator) ? source.logicOperator : 'and',
        items,
    };
    if (Array.isArray(source.quickFilterValues)) result.quickFilterValues = cloneValue(source.quickFilterValues);
    if (['and', 'or'].includes(source.quickFilterLogicOperator)) {
        result.quickFilterLogicOperator = source.quickFilterLogicOperator;
    }
    if (typeof source.quickFilterExcludeHiddenColumns === 'boolean') {
        result.quickFilterExcludeHiddenColumns = source.quickFilterExcludeHiddenColumns;
    }
    return result;
};

export const sanitizeReportTablePreferences = (value = {}, defaults = {}, { preservePage = false } = {}) => {
    const fields = getColumnFields(defaults);
    const requiredFields = getRequiredFields(defaults, fields);
    const savedOrder = uniqueKnownFields(value.columnOrder, fields);
    const fallbackOrder = uniqueKnownFields(defaults.columnOrder, fields);
    const columnOrder = [...savedOrder, ...fallbackOrder, ...fields].filter(
        (field, index, values) => values.indexOf(field) === index
    );
    const requestedPageSize = finiteNumber(value.paginationModel?.pageSize);
    const configuredPageSize = finiteNumber(defaults.paginationModel?.pageSize);
    const defaultPageSize = PAGE_SIZES.includes(configuredPageSize)
        ? configuredPageSize
        : PAGE_SIZES[0];
    const pageSize = PAGE_SIZES.includes(requestedPageSize) ? requestedPageSize : defaultPageSize;
    const requestedPage = finiteNumber(value.paginationModel?.page);
    const page = preservePage && Number.isInteger(requestedPage) && requestedPage >= 0 ? requestedPage : 0;

    const sanitized = {
        columnOrder,
        columnVisibilityModel: sanitizeVisibility(
            value.columnVisibilityModel,
            defaults.columnVisibilityModel,
            fields,
            requiredFields
        ),
        columnWidths: sanitizeWidths(value.columnWidths, defaults.columnWidths, fields),
        pinnedColumns: sanitizePins(value.pinnedColumns, defaults.pinnedColumns, fields, requiredFields),
        sortModel: sanitizeSortModel(value.sortModel, defaults.sortModel, fields),
        filterModel: sanitizeFilterModel(value.filterModel, defaults.filterModel, fields),
        paginationModel: { page, pageSize },
    };

    Object.keys(defaults).forEach((key) => {
        if (CONFIG_KEYS.has(key) || PREFERENCE_KEYS.has(key)) return;
        const extensionValue = value[key] === undefined ? defaults[key] : value[key];
        sanitized[key] = key === 'campaignGroups'
            ? sanitizeCampaignGroups(extensionValue)
            : cloneValue(extensionValue);
    });
    return sanitized;
};

export default function useReportTablePreferences(tableName, defaults) {
    const saveOwnerId = useId();
    const defaultsSignature = JSON.stringify(defaults);
    const stableDefaultsRef = useRef({ signature: defaultsSignature, value: defaults });
    if (stableDefaultsRef.current.signature !== defaultsSignature) {
        stableDefaultsRef.current = { signature: defaultsSignature, value: defaults };
    }
    const stableDefaults = stableDefaultsRef.current.value;
    const reduxDispatch = useDispatch();
    const userId = useGetCurrentUserId();
    const practiceId = useSelector((state) => state.practiceTreatmentList?.practiceId ?? null);
    const { tablePreferences, globalTablePreferences, tablePreferencesOrigin } = useSelector((state) => state.tablePreferences);
    const userRecordsRef = useRef(tablePreferences);
    const globalRecordsRef = useRef(globalTablePreferences);
    const saveTimerRef = useRef(null);
    const scopeRef = useRef({ key: '', generation: 0, active: false });
    const trustedReduxScopeRef = useRef('');
    const authoritativeReduxSignatureRef = useRef(null);
    const recordIdRef = useRef(null);
    const observedRecordIdRef = useRef(null);
    const recordSignatureRef = useRef('');
    const globalRecordSignatureRef = useRef('');
    const dirtyRef = useRef(false);
    const revisionRef = useRef(0);
    const hydratedRef = useRef(false);
    const saveChainRef = useRef(Promise.resolve());
    const preferencesRef = useRef(sanitizeReportTablePreferences(stableDefaults, stableDefaults));
    const [preferences, setPreferences] = useState(preferencesRef.current);
    const [hydrated, setHydrated] = useState(false);
    const scopeKey = JSON.stringify({
        practiceId: practiceId ?? null,
        userId: userId || null,
        tableName,
        defaults: defaultsSignature,
    });
    const renderedScopeRef = useRef(scopeKey);
    renderedScopeRef.current = scopeKey;
    const currentScopeHydrated = hydrated && scopeRef.current.key === scopeKey;

    userRecordsRef.current = tablePreferences;
    globalRecordsRef.current = globalTablePreferences;
    hydratedRef.current = currentScopeHydrated;

    const cancelPendingSave = useCallback(() => {
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
            saveTimerRef.current = null;
        }
    }, []);

    const enqueuePersist = useCallback((nextPreferences, revision) => {
        if (!userId || !tableName || !hydratedRef.current) return Promise.resolve(null);
        const owner = { ...scopeRef.current };
        const isCurrent = () => scopeRef.current.active
            && renderedScopeRef.current === owner.key
            && scopeRef.current.key === owner.key
            && scopeRef.current.generation === owner.generation;
        const sanitized = sanitizeReportTablePreferences(nextPreferences, stableDefaults);
        const run = async () => {
            if (!isCurrent()) return null;
            const payload = {
                body: { table_name: tableName, preferences: sanitized },
                practiceUserId: userId,
                refreshOrigin: { ownerId: saveOwnerId, scopeKey: owner.key, generation: owner.generation },
                isCurrent,
            };
            const currentRecordId = recordIdRef.current;
            const response = currentRecordId !== null && currentRecordId !== undefined
                ? await reduxDispatch(handleTablePreferencesEdit({ ...payload, tableId: currentRecordId }))
                : await reduxDispatch(handleTablePreferences(payload));
            if (!isCurrent()) return response;
            const succeeded = response?.status >= 200 && response?.status < 300;
            const createdId = response?.data?.id ?? response?.id;
            if (succeeded && (currentRecordId === null || currentRecordId === undefined) && createdId !== null && createdId !== undefined) {
                recordIdRef.current = createdId;
            }
            if (succeeded && revisionRef.current === revision) {
                dirtyRef.current = false;
                recordSignatureRef.current = JSON.stringify(sanitized);
            }
            return response;
        };
        const queued = saveChainRef.current.catch(() => null).then(run);
        saveChainRef.current = queued;
        return queued;
    }, [reduxDispatch, saveOwnerId, stableDefaults, tableName, userId]);

    const queueSave = useCallback((nextPreferences) => {
        if (!hydratedRef.current) return;
        cancelPendingSave();
        const snapshot = cloneValue(nextPreferences);
        const revision = revisionRef.current;
        const owner = { ...scopeRef.current };
        saveTimerRef.current = setTimeout(() => {
            saveTimerRef.current = null;
            if (scopeRef.current.key === owner.key && scopeRef.current.generation === owner.generation) {
                enqueuePersist(snapshot, revision);
            }
        }, 300);
    }, [cancelPendingSave, enqueuePersist]);

    const updatePreferences = useCallback((updater) => {
        setPreferences((current) => {
            const next = sanitizeReportTablePreferences(updater(current), stableDefaults, { preservePage: true });
            preferencesRef.current = next;
            revisionRef.current += 1;
            dirtyRef.current = true;
            queueSave(next);
            return next;
        });
    }, [queueSave, stableDefaults]);

    useEffect(() => {
        cancelPendingSave();
        const generation = scopeRef.current.generation + 1;
        scopeRef.current = { key: scopeKey, generation, active: true };
        trustedReduxScopeRef.current = '';
        authoritativeReduxSignatureRef.current = null;
        recordIdRef.current = null;
        observedRecordIdRef.current = null;
        recordSignatureRef.current = '';
        globalRecordSignatureRef.current = '';
        dirtyRef.current = false;
        revisionRef.current = 0;
        hydratedRef.current = false;
        saveChainRef.current = Promise.resolve();
        setHydrated(false);
        const safeDefaults = sanitizeReportTablePreferences(stableDefaults, stableDefaults);
        preferencesRef.current = safeDefaults;
        setPreferences(safeDefaults);
        let active = true;

        const hydrate = async () => {
            let response = null;
            try {
                if (userId) {
                    response = await Promise.resolve(reduxDispatch(getTablePreferences({
                        practiceUserId: userId,
                        isCurrent: () => active && renderedScopeRef.current === scopeKey
                            && scopeRef.current.generation === generation,
                    })));
                }
            } catch (error) {
                response = null;
            }
            if (
                !active
                || scopeRef.current.key !== scopeKey
                || scopeRef.current.generation !== generation
            ) return;
            const hasAuthoritativeResponse = response?.status === 200;
            const responseRecords = hasAuthoritativeResponse ? response.data : undefined;
            if (hasAuthoritativeResponse) {
                authoritativeReduxSignatureRef.current = JSON.stringify(listRecords(responseRecords));
                if (
                    JSON.stringify(listRecords(userRecordsRef.current))
                    === authoritativeReduxSignatureRef.current
                ) trustedReduxScopeRef.current = scopeKey;
            }
            let userRecord = null;
            if (hasAuthoritativeResponse) {
                userRecord = recordFor(responseRecords, tableName);
            } else if (trustedReduxScopeRef.current === scopeKey) {
                userRecord = recordFor(userRecordsRef.current, tableName);
            }
            const globalRecord = recordFor(globalRecordsRef.current, tableName);
            const source = userRecord?.preferences ?? globalRecord?.preferences ?? stableDefaults;
            const next = sanitizeReportTablePreferences(source, stableDefaults);
            recordIdRef.current = userRecord?.id ?? null;
            observedRecordIdRef.current = userRecord?.id ?? null;
            recordSignatureRef.current = userRecord?.preferences
                ? JSON.stringify(userRecord.preferences)
                : '';
            globalRecordSignatureRef.current = globalRecord
                ? JSON.stringify({ id: globalRecord.id ?? null, preferences: globalRecord.preferences ?? null })
                : 'none';
            preferencesRef.current = next;
            setPreferences(next);
            hydratedRef.current = true;
            setHydrated(true);
        };

        hydrate();
        return () => {
            active = false;
            scopeRef.current.active = false;
            cancelPendingSave();
        };
    }, [cancelPendingSave, reduxDispatch, scopeKey, stableDefaults, tableName, userId]);

    const reduxUserRecord = recordFor(tablePreferences, tableName);
    const reduxRecordId = reduxUserRecord?.id ?? null;
    const reduxRecordSignature = reduxUserRecord?.preferences
        ? JSON.stringify(reduxUserRecord.preferences)
        : '';
    const reduxRecordsSignature = JSON.stringify(listRecords(tablePreferences));
    const reduxGlobalRecord = recordFor(globalTablePreferences, tableName);
    const reduxGlobalRecordSignature = reduxGlobalRecord
        ? JSON.stringify({ id: reduxGlobalRecord.id ?? null, preferences: reduxGlobalRecord.preferences ?? null })
        : 'none';

    useEffect(() => {
        if (
            hydrated
            && authoritativeReduxSignatureRef.current !== null
            && authoritativeReduxSignatureRef.current === reduxRecordsSignature
        ) trustedReduxScopeRef.current = scopeKey;
        if (!hydrated || trustedReduxScopeRef.current !== scopeKey) return;

        // Only the publication caused by this hook's save is an echo. Values
        // are not ownership evidence: independent updates may reuse any value.
        // Scope ownership also rejects an old save's refresh after a handoff.
        if (tablePreferencesOrigin?.ownerId === saveOwnerId) {
            if (tablePreferencesOrigin.scopeKey === scopeKey
                && tablePreferencesOrigin.generation === scopeRef.current.generation
                && reduxUserRecord
                && (recordIdRef.current === reduxRecordId || (recordIdRef.current === null && dirtyRef.current))) {
                observedRecordIdRef.current = reduxRecordId;
            }
            return;
        }

        if (!reduxUserRecord) {
            const observedId = observedRecordIdRef.current;
            if (observedId === null || recordIdRef.current !== observedId) return;

            cancelPendingSave();
            recordIdRef.current = null;
            observedRecordIdRef.current = null;
            recordSignatureRef.current = '';
            if (dirtyRef.current) {
                queueSave(preferencesRef.current);
                return;
            }

            const source = reduxGlobalRecord?.preferences ?? stableDefaults;
            const next = sanitizeReportTablePreferences(source, stableDefaults);
            globalRecordSignatureRef.current = reduxGlobalRecordSignature;
            preferencesRef.current = next;
            setPreferences(next);
            return;
        }

        const identityChanged = recordIdRef.current !== reduxRecordId;
        if (identityChanged) {
            cancelPendingSave();
            recordIdRef.current = reduxRecordId;
            if (dirtyRef.current) queueSave(preferencesRef.current);
        }
        observedRecordIdRef.current = reduxRecordId;

        if (!dirtyRef.current && reduxRecordSignature !== recordSignatureRef.current) {
            const next = sanitizeReportTablePreferences(reduxUserRecord.preferences, stableDefaults);
            recordSignatureRef.current = reduxRecordSignature;
            preferencesRef.current = next;
            setPreferences(next);
        }
    }, [
        cancelPendingSave,
        hydrated,
        queueSave,
        reduxRecordId,
        reduxRecordSignature,
        reduxRecordsSignature,
        reduxGlobalRecord,
        reduxGlobalRecordSignature,
        reduxUserRecord,
        scopeKey,
        saveOwnerId,
        stableDefaults,
        tablePreferencesOrigin,
    ]);

    useEffect(() => {
        if (
            !hydrated
            || dirtyRef.current
            || recordIdRef.current !== null
            || reduxGlobalRecordSignature === globalRecordSignatureRef.current
        ) return;

        const source = reduxGlobalRecord?.preferences ?? stableDefaults;
        const next = sanitizeReportTablePreferences(source, stableDefaults);
        globalRecordSignatureRef.current = reduxGlobalRecordSignature;
        preferencesRef.current = next;
        setPreferences(next);
    }, [
        hydrated,
        reduxGlobalRecord,
        reduxGlobalRecordSignature,
        stableDefaults,
    ]);

    const save = useCallback(async () => {
        cancelPendingSave();
        return enqueuePersist(preferencesRef.current, revisionRef.current);
    }, [cancelPendingSave, enqueuePersist]);

    const reset = useCallback(async () => {
        cancelPendingSave();
        const next = sanitizeReportTablePreferences(stableDefaults, stableDefaults);
        preferencesRef.current = next;
        revisionRef.current += 1;
        dirtyRef.current = true;
        setPreferences(next);
        return enqueuePersist(next, revisionRef.current);
    }, [cancelPendingSave, enqueuePersist, stableDefaults]);

    const onColumnOrderChange = useCallback((columnOrder) => {
        updatePreferences((current) => ({ ...current, columnOrder }));
    }, [updatePreferences]);

    const onColumnVisibilityModelChange = useCallback((columnVisibilityModel) => {
        updatePreferences((current) => ({ ...current, columnVisibilityModel }));
    }, [updatePreferences]);

    const onColumnWidthsChange = useCallback((columnWidths) => {
        updatePreferences((current) => ({
            ...current,
            columnWidths: { ...current.columnWidths, ...columnWidths },
        }));
    }, [updatePreferences]);

    const onPinnedColumnsChange = useCallback((pinnedColumns) => {
        updatePreferences((current) => ({ ...current, pinnedColumns }));
    }, [updatePreferences]);

    const onSortModelChange = useCallback((sortModel) => {
        updatePreferences((current) => ({ ...current, sortModel }));
    }, [updatePreferences]);

    const onFilterModelChange = useCallback((filterModel) => {
        updatePreferences((current) => ({ ...current, filterModel }));
    }, [updatePreferences]);

    const onPaginationModelChange = useCallback((paginationModel) => {
        updatePreferences((current) => ({ ...current, paginationModel }));
    }, [updatePreferences]);

    const onExtensionChange = useCallback((key, value) => {
        if (
            !Object.prototype.hasOwnProperty.call(stableDefaults, key)
            || CONFIG_KEYS.has(key)
            || PREFERENCE_KEYS.has(key)
        ) return false;
        updatePreferences((current) => ({ ...current, [key]: cloneValue(value) }));
        return true;
    }, [stableDefaults, updatePreferences]);

    return useMemo(() => ({
        preferences,
        hydrated: currentScopeHydrated,
        columnOrder: preferences.columnOrder,
        columnVisibilityModel: preferences.columnVisibilityModel,
        columnWidths: preferences.columnWidths,
        pinnedColumns: preferences.pinnedColumns,
        sortModel: preferences.sortModel,
        filterModel: preferences.filterModel,
        paginationModel: preferences.paginationModel,
        onColumnOrderChange,
        onColumnVisibilityModelChange,
        onColumnWidthsChange,
        onPinnedColumnsChange,
        onSortModelChange,
        onFilterModelChange,
        onPaginationModelChange,
        onExtensionChange,
        save,
        reset,
    }), [
        currentScopeHydrated,
        onColumnOrderChange,
        onColumnVisibilityModelChange,
        onColumnWidthsChange,
        onFilterModelChange,
        onExtensionChange,
        onPaginationModelChange,
        onPinnedColumnsChange,
        onSortModelChange,
        preferences,
        reset,
        save,
    ]);
}
